import { IAction } from '@Root/types'

import {
  IElementData,
} from '@SiteModules/Element/types'

import {
  ISetupProjectBasePayload,
} from '@SiteModules/Project/types'

export const ProjectVersionSettingsActionTypes = {
  SUBMIT_PROJECT_VERSION_UPDATE: '@@projectVersionSettings/SUBMIT_PROJECT_VERSION_UPDATE',
  SUBMIT_PROJECT_VERSION_UPDATE_SUCCESS: '@@projectVersionSettings/SUBMIT_PROJECT_VERSION_UPDATE_SUCCESS',
  SUBMIT_PROJECT_VERSION_UPDATE_FAILURE: '@@projectVersionSettings/SUBMIT_PROJECT_VERSION_UPDATE_FAILURE',
  BEGIN_PROJECT_SUBMISSION: '@@projectVersionSettings/BEGIN_PROJECT_SUBMISSION',
  COMPLETE_PROJECT_SUBMISSION: '@@projectVersionSettings/COMPLETE_PROJECT_SUBMISSION',
  FAIL_PROJECT_SUBMISSION: '@@projectVersionSettings/FAIL_PROJECT_SUBMISSION',
  FETCH_PROJECT_VERSION_SETTINGS: '@@projectVersionSettings/FETCH_PROJECT_VERSION_SETTINGS',
  FETCH_PROJECT_VERSION_SETTINGS_SUCCESS: '@@projectVersionSettings/FETCH_PROJECT_VERSION_SETTINGS_SUCCESS',
  PROJECT_VERSION_SETTINGS_FAILURE: '@@projectVersionSettings/PROJECT_VERSION_SETTINGS_FAILURE',
  RESET_PROJECT_VERSION_SETTINGS: '@@projectVersionSettings/RESET_PROJECT_VERSION_SETTINGS',
  ADD_ELEMENTS_FOR_PROJECT_VERSIONS: '@@projectVersionSettings/ADD_ELEMENTS_FOR_PROJECT_VERSIONS',
  REMOVE_FROM_ELEMENTS_FOR_PROJECT_VERSIONS: '@@projectVersionSettings/REMOVE_FROM_ELEMENTS_FOR_PROJECT_VERSIONS',
  SET_VALUES_AFTER_UPLOAD: '@@projectVersionSettings/SET_VALUES_AFTER_UPLOAD',
  ADD_PROJECT_ID_PENDING_CLEANUP: '@@projectVersionSettings/ADD_PROJECT_ID_PENDING_CLEANUP',
  RELEASE_PROJECT_CLEANUP_OWNERSHIP: '@@projectVersionSettings/RELEASE_PROJECT_CLEANUP_OWNERSHIP',
  SET_SIZE_LIMITS_ALERT_AT_SUBMIT_URL: '@@projectVersionSettings/SET_SIZE_LIMITS_ALERT_AT_SUBMIT_URL',
  RESET_SIZE_LIMITS_ALERT_AT_SUBMIT_URL: '@@projectVersionSettings/RESET_SIZE_LIMITS_ALERT_AT_SUBMIT_URL',
  REMOVE_FROM_SUBMITTING_URLS: '@@projectVersionSettings/REMOVE_FROM_SUBMITTING_URLS',
  RESET_SUBMITTING_URLS: '@@projectVersionSettings/RESET_SUBMITTING_URLS',
  UPDATE_DOWNLOADING_ELEMENTS: '@@projectVersionSettings/UPDATE_DOWNLOADING_ELEMENTS',
  UPDATE_DOWNLOADING_ELEMENTS_SUCCESS: '@@projectVersionSettings/UPDATE_DOWNLOADING_ELEMENTS_SUCCESS',
  UPDATE_DOWNLOADING_ELEMENTS_FAILURE: '@@projectVersionSettings/UPDATE_DOWNLOADING_ELEMENTS_FAILURE',
  FETCH_TRANSLATION_LANGUAGES: '@@projectVersionSettings/FETCH_TRANSLATION_LANGUAGES',
  FETCH_TRANSLATION_LANGUAGES_SUCCESS: '@@projectVersionSettings/FETCH_TRANSLATION_LANGUAGES_SUCCESS',
  FETCH_TRANSLATION_LANGUAGES_FAILURE: '@@projectVersionSettings/FETCH_TRANSLATION_LANGUAGES_FAILURE',
  RESET_TRANSLATION_LANGUAGES: '@@projectVersionSettings/RESET_TRANSLATION_LANGUAGES'
}

export interface IProjectVersionSettingsState {
  readonly settings: IProjectVersionSettingsAttributes
  readonly availableLanguages: IAvailableLanguagesAttributes
  readonly lastUsedSettings: ILastUsedSettingsAttributes
  readonly hasLoadedSuccessfully: boolean
  readonly elementsForProjectVersions: IElementForProjectVersion[]
  readonly projectIdsPendingCleanup: string[]
  readonly projectCleanupOwnership: 'setup' | 'submission' | 'released'
  readonly fetchingTranslationLanguages: boolean
  readonly submitUrl: {
    readonly showSizeLimitsAlert: boolean
    readonly submittingUrls: ISubmittingUrl[]
  }
}

export interface ISubmittingUrl {
  identifier: string
  url: string
}

export interface IProjectVersionSettingsAttributes {
  readonly language: string
  readonly subtitleLanguages: string[]
  readonly duration: number
  readonly hasAudioSeparation: boolean
  readonly subscriptionSubtitlesLength: number
  readonly hasVoice: boolean
  readonly projectTermbaseName?: string
}

export interface IAvailableLanguagesAttributes extends IAvailableBaseLanguagesAttributes {
  readonly alignmentLanguages: string[]
}

export interface IAvailableBaseLanguagesAttributes {
  readonly languagesForVideos: string[]
  readonly translationLanguages: string[]
  readonly nonTranslatableLanguages: string[]
  readonly dubbingLanguages: string[]
  readonly voiceCloningLanguages: string[]
}

export interface ILastUsedSettingsAttributes {
  readonly lastUsedTranscriptInformation?: ITranscriptInformation
  readonly lastUsedTranslationInformation?: ITranslationInformation
  readonly lastUsedDubbingInformation?: IDubbingInformation
  readonly lastUsedRepurposeInformation?: IRepurposeInformation
  readonly lastUsedTermbaseName?: string
  readonly lastUsedThemeName?: string
  readonly hasNoLastCreatedSubtitle?: boolean
}

export interface ITranscriptInformation {
  readonly language?: string
  readonly subtitleLanguages?: string[]
  readonly vocabularyName?: string
  readonly guidelineName?: string
  readonly createdAfterGuidelineDeployment?: boolean
  readonly speakerDiarization?: boolean
}

export interface ITranslationInformation {
  readonly lastUsedReviewTranscript: boolean
  readonly guidelineName?: string
  readonly createdAfterGuidelineDeployment?: boolean
  readonly lastUsedFormality?: string
}

export interface IDubbingInformation {
  readonly lastUsedSeparateAudio?: boolean
  readonly lastUsedAdaptTranslation?: boolean
  readonly lastUsedLipSyncModel?: string
  readonly lastUsedReviewForDubbing: string[]
}

interface IEnhanceOptionsPayload {
  readonly enabled?: boolean
  readonly preProcessing?: boolean
  readonly postProcessing?: boolean
}

export interface IRepurposeInformation {
  readonly lastUsedReviewForRepurpose: string[]
}

export interface ISubmitProjectVersionUpdatePayload extends ISetupProjectBasePayload {
  readonly workspaceId: number
  readonly vocabularyIds: number[]
  readonly projectVersionIds: string[]
  readonly includesTranscript: boolean
  readonly transcriptText?: string
  readonly srtTranscriptText?: string
  readonly enableSpeakerDiarization: boolean
  readonly dubbing: boolean
  readonly repurposing: boolean
  readonly generateShortenedProjectVersion: boolean
  readonly reviewTranscriptBeforeTranslation: boolean
  readonly createCustomVoices?: boolean
  readonly voiceEngine?: string
  readonly shouldSeparateAudio?: boolean
  readonly adaptTranslation?: boolean
  readonly odubModelId?: string
  readonly lipSyncModel?: string
  readonly enhanceOptions?: IEnhanceOptionsPayload
  readonly captionGuidelineId?: number
  readonly outputFormat?: string
  readonly reviewTranslationBeforeDubbing?: boolean
}

export interface IFetchProjectVersionSettingsPayload {
  readonly workspaceId: number
  readonly projectVersionIds?: string[]
}

export interface IFetchProjectVersionSettingsSuccessResponseBody {
  readonly projectVersionSettings: IProjectVersionSettingsData
  readonly availableLanguages: IAvailableLanguagesData
  readonly lastUsedSettings: ILastUsedSettingsData
}

export interface IProjectVersionSettingsData {
  readonly id?: string
  readonly type?: 'projectVersionSettings'
  readonly attributes: IProjectVersionSettingsAttributes | IProjectVersionDuration
}

export interface IProjectVersionDuration {
  readonly duration: number
}

export interface IAvailableLanguagesData {
  readonly id: string
  readonly type: 'availableLanguages'
  readonly attributes: IAvailableLanguagesAttributes
}

export interface IAvailableLanguagesBaseData {
  readonly id: string
  readonly type: 'availableLanguages'
  readonly attributes: IAvailableBaseLanguagesAttributes
}

export interface ILastUsedSettingsData {
  readonly id: string
  readonly type: 'lastUsedSettings'
  readonly attributes: ILastUsedSettingsAttributes
}

export interface IProjectVersionSettingsParameterOption {
  readonly name: string
  readonly icon?: JSX.Element
  readonly number?: number
  readonly trailingIcon?: JSX.Element
  readonly description?: string
}

export interface IProjectVersionSettingsReviewParameterOption {
  readonly name: string
  readonly nameToShowInDropdown: string
  readonly nameToShowInButton?: string
  readonly description?: string
  readonly disabled?: boolean
  /** Shown under the description when the option is disabled (e.g. dependency hint). */
  readonly disabledHint?: string
}

export interface IProjectVersionSettingsParameter {
  readonly name: string
  readonly options: IProjectVersionSettingsParameterOption[]
  readonly selectedOptionName: string | null
  readonly selectedOptionNameButton?: string | null
  readonly dropdownDirection?: 'auto' | 'up'
  readonly shouldHide: boolean
  readonly icon?: JSX.Element
  readonly searchPlaceholder?: string
  readonly largeIcon?: boolean
  readonly nullOption?: IProjectVersionSettingsParameterOption
  readonly showAsAdvanced?: boolean
  readonly isActive?: boolean
  readonly tooltipText?: string
  setSelectedOption(newSelectedOption: string | null): void
  specialButtonClickBehaviour?(): void
}

export interface IElementForProjectVersion {
  readonly elementId: string
  readonly name: string
  readonly size: number
  readonly projectVersionId?: string
  readonly projectId?: string
  readonly duration?: number
  readonly downloading?: boolean
  readonly thumbnailUrl?: string
}

export interface IAddElementsForProjectVersionsPayload {
  readonly elementsToAdd: IElementForProjectVersion[]
}

export interface IRemoveFromElementsForProjectVersionsPayload {
  readonly elementId: string
}

export interface ISetValuesAfterUploadPayload {
  readonly elementId: string
  readonly projectVersionId: string
  readonly projectId: string
  readonly duration: number
}

export interface IAddProjectIdPendingCleanupPayload {
  readonly projectId: string
}

export interface IRemoveFromSubmittingUrlsPayload {
  readonly submitUrlIdentifier: string
}

export interface IUpdateDownloadingElementsPayload {
  readonly elementsToUpdateIds: string[]
  readonly workspaceId: number
  readonly requestFromDashboard: boolean
}

export interface IUpdateDownloadingElementsSuccessResponseBody {
  data: IElementData[]
}

export interface IFetchTranslationLanguagesPayload {
  readonly projectVersionLanguage: string
  readonly workspaceId: number
  readonly projectVersionIds?: string[]
}

export interface IFetchTranslationLanguagesSuccessResponseBody {
  readonly translationLanguages: string[]
}

export type TProjectVersionSettingsActionTypes =
  IAction<IFetchProjectVersionSettingsPayload> |
  IAction<IFetchProjectVersionSettingsSuccessResponseBody> |
  IAction<IAddElementsForProjectVersionsPayload> |
  IAction<IRemoveFromElementsForProjectVersionsPayload> |
  IAction<ISetValuesAfterUploadPayload> |
  IAction<IAddProjectIdPendingCleanupPayload> |
  IAction<IRemoveFromSubmittingUrlsPayload> |
  IAction<IUpdateDownloadingElementsSuccessResponseBody> |
  IAction<IFetchTranslationLanguagesSuccessResponseBody>
